import { SupplierAllocation } from '../../models/supplier-allocation.model';

/**
 * Whether an allocation can take a share of the survey's completes.
 *
 * Ported from the legacy validity gate (`cpi == 0 && !group_ref` → excluded): a
 * standalone supplier with no rate has nothing to price against, while a group
 * is always allocatable because its members carry the rates. The CPI checked
 * here is deliberately the supplier's own contracted rate, never the
 * feasibility response's — feasibility is display data and can report 0 for a
 * supplier that is perfectly well priced.
 *
 * Lives in utils rather than beside the drawer because both the drawer's row
 * mapper (ui) and the feasibility recalculation effect (data-access) must agree
 * on it, and data-access cannot import ui.
 */
export function isAllocatableSupplier(allocation: SupplierAllocation): boolean {
  return (
    allocation.isActive &&
    allocation.quantity > 0 &&
    (allocation.isGroup || allocation.cpi !== 0)
  );
}

/**
 * Splits `totalCompletes` evenly across `supplierIds`, floor-division with the
 * remainder going to the earliest ids — the returned values always sum to
 * `totalCompletes` exactly (never over-allocates), and an empty id list
 * distributes to an empty map.
 *
 * Sits beside `isAllocatableSupplier` because the two answer one question
 * together: which suppliers take work, and how much each one takes.
 */
export function distributeCompletes(
  totalCompletes: number,
  supplierIds: readonly string[],
): ReadonlyMap<string, number> {
  if (!supplierIds.length) return new Map();
  const base = Math.floor(totalCompletes / supplierIds.length);
  const remainder = totalCompletes % supplierIds.length;
  return new Map(
    supplierIds.map((id, index) => [id, base + (index < remainder ? 1 : 0)]),
  );
}
